import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { Maximize2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/contexts/I18nContext';
import {
  getTablesStrips,
  whDomain,
  type TableStripSegment,
  type TableStripState,
  type TableStripTable,
} from '@/api/analytics';
import { DayChartLightbox } from './day/DayChartLightbox';
import { type DayStripPhoto } from './day/DayPhotoStrip';
import { makeTimeAxis } from './day/timeAxis';
import { origAspect as origAspectRaw, origGeomFromPolygon, type OrigGeom } from './day/OrigCropView';

// Пропорции рамки «в оригинальных пропорциях» — по прямоугольнику стола, ОБРЕЗАННОМУ краем кадра,
// как его режет сервер (table_frame_crop): у столов, чей полигон выходит за кадр, рамка была выше
// картинки — пустые поля сверху/снизу (Алексей 28.10 #3132).
const clip01 = (v: number) => Math.min(1, Math.max(0, v));
function origBoxAspect(g: OrigGeom, frameAspect: number): number {
  const b = g.bbox;
  return origAspectRaw({ ...g, bbox: { x0: clip01(b.x0), y0: clip01(b.y0), x1: clip01(b.x1), y1: clip01(b.y1) } }, frameAspect);
}
const fmtDirtyFor = (s: number | null | undefined) => {
  if (s == null || s < 60) return '';
  const m = Math.round(s / 60);
  return m < 60 ? ` ${m} мин` : ` ${Math.floor(m / 60)} ч${m % 60 ? ` ${m % 60} мин` : ''}`;
};
import { cn } from '@/lib/utils';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { Tooltip } from '@/components/ui/tooltip';

// Per-table state strips — "график состояния столов". One colored band per
// table across the period: gray = free & clean, green = occupied (people at
// the table), red = left uncleaned. Hover a run for its exact span; click a
// table's row to open it fullscreen with the dirty-incident crops (start/end
// per incident) and the usual marquee zoom.

const SUB_REASON_KEY: Record<string, string> = {
  llm_items: 'tables.reason.guestItems',
  llm_clear: 'tables.reason.clean',
  glare: 'tables.spoiledReason.glare',
  obstructed: 'tables.spoiledReason.obstructed',
};

const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

// «Фото в оригинальных пропорциях» для столов (Алексей 26.10 #2638) — как у стеллажей: описывающий
// прямоугольник полигона стола, вырезанный из кадра. Свой ключ — режим помнится отдельно от выкладки.
const ORIG_ASPECT_KEY = 'tables.photos.origAspect';
const FRAME_ASPECT = 16 / 9;
const THUMB_H = 40; // px — h-10
const THUMB_MAX_W = 72;

const STATE_COLORS: Record<TableStripState, string> = {
  free: 'var(--color-muted-foreground)',
  occupied: '#10b981', // emerald — same accent as the kitchen line
  dirty: '#ef4444', // red — attention
  shifted: '#f59e0b', // amber — «стол не на месте», worth a look but not dirt
  no_data: 'transparent',
  unknown: '#94a3b8', // slate — «нельзя определить» (несколько столов / перекрыт)
};
const STATE_OPACITY: Record<TableStripState, number> = {
  free: 0.18,
  occupied: 0.85,
  dirty: 0.9,
  shifted: 0.8,
  unknown: 0.55,
  no_data: 0,
};
const STATE_LABEL_KEY: Record<TableStripState, string> = {
  free: 'report.tables.stateFree',
  occupied: 'report.tables.stateOccupied',
  dirty: 'report.tables.stateDirty',
  shifted: 'report.tables.stateShifted',
  unknown: 'report.tables.stateUnknown',
  no_data: 'dashboard.status.noData',
};

function fmtClock(ms: number): string {
  // Pinned to MSK — see the note on useFrameTime (hover-time.tsx).
  return new Date(ms).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
}

function fmtDuration(t: (k: string, p?: Record<string, string | number>) => string, ms: number): string {
  const mins = Math.max(1, Math.round(ms / 60000));
  if (mins < 60) return t('report.tables.durationMin', { mins });
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? t('report.tables.durationHM', { h, m }) : t('report.tables.durationH', { h });
}

interface HoverInfo {
  x: number;
  y: number;
  /** 'nodata' = uncovered stretch inside working hours (no samples there);
   *  'offhours' = the zone simply wasn't open. */
  state: TableStripState | 'nodata' | 'offhours';
  startMs?: number;
  endMs?: number;
  /** state 'unknown': причина (several_tables | obstructed | glare). */
  reason?: string | null;
  /** Новая система: пояснение к состоянию («вещи гостя»). */
  sub?: string | null;
}

/** One table's band for a given [domain] window; segments outside are clipped.
 *  Shared between the card rows and the fullscreen (bigger) view. */
function StripBand({
  segments,
  domain,
  height,
  onHover,
  onPick,
  workWindows,
  className,
}: {
  segments: TableStripSegment[];
  domain: [number, number];
  height: number;
  onHover?: (info: HoverInfo | null) => void;
  /** Click anywhere on the band → the clicked MOMENT (ms). */
  onPick?: (tMs: number) => void;
  /** Per-day working windows (ms); time outside them reads «нерабочее время»
   *  instead of «нет данных», and gets a visibly darker band. */
  workWindows?: Array<[number, number]>;
  className?: string;
}) {
  const { t } = useT();
  const [d0, d1] = domain;
  const span = Math.max(d1 - d0, 1);
  // Hovered segment index — the run under the pointer brightens so the
  // tooltip's subject is visually pinned.
  const [hotSeg, setHotSeg] = useState<number | null>(null);
  // Handlers live on the band root, so currentTarget is always the band.
  const timeAtClientX = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(Math.max((e.clientX - r.left) / Math.max(r.width, 1), 0), 1);
    return d0 + frac * span;
  };
  const inWork = (ms: number) =>
    !workWindows?.length || workWindows.some(([lo, hi]) => ms >= lo && ms <= hi);
  return (
    <div
      // Card-background band: uncovered time (no data OR closed hours) reads
      // as plain white — only the tooltip differentiates the two.
      className={cn('relative w-full overflow-hidden rounded', className)}
      style={{ height }}
      onMouseLeave={() => {
        setHotSeg(null);
        onHover?.(null);
      }}
      // Background (uncovered stretch): inside working hours it's a genuine
      // data gap; outside — the zone was simply closed. Segments stop
      // propagation, so this only fires on the background.
      onMouseMove={
        onHover
          ? (e) =>
              onHover({
                x: e.clientX,
                y: e.clientY,
                state: inWork(timeAtClientX(e)) ? 'nodata' : 'offhours',
              })
          : undefined
      }
      onClick={
        onPick
          ? (e) => {
              // Keep the row button's own onClick (full-range open) out of it.
              e.stopPropagation();
              onPick(timeAtClientX(e));
            }
          : undefined
      }
    >
      {/* Off-hours carry NO shading of their own (same look as no-data —
          the darker overlay made 'free' read as two different grays); the
          hover tooltip says «Нерабочее время». */}
      {segments.map((s, i) => {
        const sMs = new Date(s.start).getTime();
        const eMs = new Date(s.end).getTime();
        if (!Number.isFinite(sMs) || !Number.isFinite(eMs) || eMs <= d0 || sMs >= d1) return null;
        const left = (Math.max(sMs, d0) - d0) / span;
        const right = (Math.min(eMs, d1) - d0) / span;
        return (
          <div
            key={i}
            role="img"
            aria-label={`${t(STATE_LABEL_KEY[s.state])} · ${fmtClock(sMs)}–${fmtClock(eMs)}`}
            className="absolute bottom-0 top-0 transition-[filter,opacity] duration-100"
            style={{
              left: `${left * 100}%`,
              width: `${Math.max((right - left) * 100, 0.15)}%`,
              background: s.hatched
                ? `repeating-linear-gradient(135deg, ${STATE_COLORS[s.state]} 0 3px, transparent 3px 7px)`
                : STATE_COLORS[s.state],
              opacity: hotSeg === i ? Math.min(STATE_OPACITY[s.state] + 0.15, 1) : STATE_OPACITY[s.state],
              filter: hotSeg === i ? 'brightness(1.15)' : undefined,
            }}
            onMouseMove={
              onHover
                ? (e) => {
                    e.stopPropagation();
                    setHotSeg(i);
                    const state = s.state === 'no_data'
                      ? (inWork(timeAtClientX(e)) ? 'nodata' : 'offhours') : s.state;
                    onHover({ x: e.clientX, y: e.clientY, state, startMs: sMs, endMs: eMs, reason: s.reason, sub: s.sub_reason });
                  }
                : undefined
            }
            onMouseLeave={() => setHotSeg((h) => (h === i ? null : h))}
          />
        );
      })}
    </div>
  );
}

/** Strip time axis on the shared ticks/labels (timeAxis.ts makeTimeAxis):
 *  one day → clock ticks; a domain across midnight (week, 2-day pick, zoom
 *  over midnight) → «пн 20.10» at midnights and «20.10 14:00» for a day's
 *  first tick, so every stretch is tied to a date (Alexey 26.10 #2646 — the
 *  old version gave a 1–2-day window bare HH:MM). */
function StripAxis({ domain }: { domain: [number, number] }) {
  const [d0, d1] = domain;
  const span = d1 - d0;
  const axis = makeTimeAxis(domain, 8);
  let ticks: { frac: number; label: string }[] =
    span > 0 ? (axis.ticks ?? []).map((ms) => ({ frac: (ms - d0) / span, label: axis.format(ms) })) : [];
  // Подписи есть всегда (Алексей 28.10 #3076): окно короче пары минут — начало, середина и конец
  // с секундами; крайние подписи не уезжают за край.
  if (span > 0 && ticks.length < 3) {
    const sec = (ms: number) => new Date(ms).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Moscow' });
    ticks = [0, 0.5, 1].map((frac) => ({ frac, label: sec(d0 + frac * span) }));
  }
  return (
    <div className="relative h-4 text-[10px] text-muted-foreground">
      {ticks.map((tk, i) => (
        <span
          key={i}
          className={cn('absolute whitespace-nowrap', tk.frac <= 0.02 ? '' : tk.frac >= 0.98 ? '-translate-x-full' : '-translate-x-1/2')}
          style={{ left: `${tk.frac * 100}%` }}
        >
          {tk.label}
        </span>
      ))}
    </div>
  );
}

function StateLegend() {
  const { t } = useT();
  const items: { state: TableStripState; label: string }[] = [
    { state: 'free', label: t('report.tables.stateFree') },
    { state: 'occupied', label: t('report.tables.stateOccupied') },
    { state: 'dirty', label: t('report.tables.stateDirty') },
    { state: 'shifted', label: t('report.tables.stateShifted') },
    { state: 'unknown', label: t('report.tables.stateUnknown') },
    { state: 'no_data', label: t('dashboard.status.noData') },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
      {items.map((it) => (
        <span key={it.state} className="inline-flex items-center gap-1.5">
          <span
            className={cn('inline-block h-2.5 w-4 rounded-sm', it.state === 'no_data' && 'border border-dashed border-border')}
            style={{ background: STATE_COLORS[it.state], opacity: it.state === 'no_data' ? 1 : STATE_OPACITY[it.state] }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// Миниатюры не налезают друг на друга: не ближе 6 % шкалы, «не убран» в приоритете.
function thin<T extends { at: string; dirty?: boolean }>(photos: T[], domain: [number, number]): T[] {
  const span = domain[1] - domain[0];
  const kept: { t: number; p: T }[] = [];
  const sorted = [...photos].sort((a, b) => Number(!!b.dirty) - Number(!!a.dirty));
  for (const p of sorted) {
    const t = new Date(p.at).getTime();
    if (!Number.isFinite(t)) continue;
    if (kept.every((k) => Math.abs(k.t - t) / span >= 0.06)) kept.push({ t, p });
  }
  return kept.sort((a, b) => a.t - b.t).map((k) => k.p);
}

/** Ряд миниатюр над полоской стола: 6 кадров, равномерно по периоду (тот же подбор, что
 *  в полноэкранном виде), каждый — над своим моментом на шкале; «не убран» — оранжевая черта.
 *  Клик — это фото в лайтбоксе (листаются миниатюры ряда; Алексей 26.10 #2644: раньше открывался весь
 *  период), удержание — полный кадр с контуром стола. Грузится отдельно на каждый стол. */
function TableThumbs({ zoneId, from, to, table, domain, origGeom }: {
  zoneId: string; from: string; to: string; table: TableStripTable; domain: [number, number];
  /** Режим «в оригинальных пропорциях» (#2638): прямоугольник стола на кадре. */
  origGeom?: OrigGeom | null;
}) {
  const { data } = useSWR(
    ['analytics:tables-thumbs', zoneId, from, to, table.id],
    () => getTablesStrips(zoneId, from, to, table.id, { photoCount: 6 }).then((r) => r.data.tables[0]?.photos ?? []),
    { revalidateOnFocus: false },
  );
  const span = domain[1] - domain[0];
  // Кадр показываем, только когда он загрузился: пустая кнопка рисовалась тонкой чертой.
  const [ok, setOk] = useState<Record<string, boolean>>({});
  const [lb, setLb] = useState<number | null>(null);
  const shown = thin(data ?? [], domain).filter((ph) => Number.isFinite(new Date(ph.at).getTime()) && ph.image_url);
  const poly = table.polygon && table.polygon.length >= 3 ? [table.polygon] : null;
  const bboxCrop = (frame: string) => {
    if (!origGeom) return null;
    const { x0, y0, x1, y1 } = origGeom.bbox;
    return { frameSrc: frame, polygons: [[[x0, y0], [x1, y0], [x1, y1], [x0, y1]]] };
  };
  // Размер миниатюры в пропорциях стола на кадре: высота 40, шире 72 — ужимаем высоту.
  let origSize: { w: number; h: number } | null = null;
  if (origGeom) {
    const a = origBoxAspect(origGeom, FRAME_ASPECT);
    origSize = THUMB_H * a > THUMB_MAX_W ? { w: THUMB_MAX_W, h: THUMB_MAX_W / a } : { w: THUMB_H * a, h: THUMB_H };
  }
  return (
    <div className="relative h-11 min-w-0 flex-1">
      {!data && <div className="absolute inset-y-1 left-0 right-0 animate-pulse rounded bg-muted/60" />}
      {shown.map((ph, i) => {
        const at = new Date(ph.at).getTime();
        const x = Math.min(1, Math.max(0, (at - domain[0]) / span));
        return (
          <Tooltip key={`${ph.at}-${i}`} content={new Date(ph.at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}><button type="button" onClick={() => setLb(i)}
            // Невидима, пока не загрузилась (не hidden: OrigCropView без кадра меряет свой размер).
            className={cn(
              // Высота кнопки — по картинке: широкий кроп, ужатый до 72 px, не оставляет пустой полосы
              // над собой (Алексей 28.10 #3132); в «оригинальных пропорциях» размер задаёт origSize.
              'absolute bottom-0 -translate-x-1/2 overflow-hidden rounded outline-none ring-1 ring-border/60 transition hover:z-10 hover:scale-[1.6] hover:ring-primary/60',
              origSize && 'h-10',
              !ok[`${ph.at}-${i}`] && 'pointer-events-none opacity-0',
            )}
            style={{
              left: `clamp(28px, ${x * 100}%, calc(100% - 28px))`, transformOrigin: 'bottom center',
              ...(origSize ? { width: origSize.w, height: origSize.h } : null),
            }}>
            {origGeom ? (
              // Готовая картинка с сервера: свой кадр или развёрнутый кроп на чёрном (#3082).
              <img key="orig" src={ph.orig_image_url ?? ph.image_url ?? undefined} alt="" className="block size-full object-cover"
                onLoad={() => setOk((m) => ({ ...m, [`${ph.at}-${i}`]: true }))} />
            ) : (
            <img src={ph.image_url ?? undefined} alt="" className="block h-auto max-h-10 w-auto max-w-[72px]"
              onLoad={() => setOk((m) => ({ ...m, [`${ph.at}-${i}`]: true }))} />
            )}
            {ph.dirty && <span className="absolute inset-x-0 bottom-0 h-1 bg-orange-500" />}
          </button></Tooltip>
        );
      })}
      {lb != null && shown.length > 0 && (
        <ImageLightbox
          srcs={shown.map((ph) => (origGeom ? ph.orig_image_url ?? ph.image_url : ph.image_url))}
          captions={shown.map((ph) => `${table.name}${ph.dirty ? ` · не убран${fmtDirtyFor(ph.segment_s)}` : ''} · ${new Date(ph.at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' })}`)}
          frameSrcs={shown.map((ph) => (poly ? ph.frame_image_url ?? null : null))}
          polygonsList={shown.map((ph) => (poly && ph.frame_image_url ? poly : null))}
          cropSpecs={shown.map((ph) => (ph.frame_image_url ? bboxCrop(ph.frame_image_url) : null))}
          primary="crop"
          holdHint="Удерживайте — полный кадр"
          index={Math.min(lb, shown.length - 1)}
          onNavigate={(i) => { if (i >= 0 && i < shown.length) setLb(i); }}
          onClose={() => setLb(null)}
        />
      )}
    </div>
  );
}

export function TableStateStrips({
  zoneId,
  from,
  to,
  title,
  experimental = false,
  deepLink = false,
  thumbs = true,
}: {
  zoneId: string;
  from: string;
  to: string;
  title: string;
  /** Show the "new feature, may be unstable" note next to the title. */
  experimental?: boolean;
  /** Mirror the open table + fullscreen zoom window into the URL (?table=&
   *  from_ts=&to_ts=) so an incident is shareable, and restore it on load.
   *  Opt-in — only the (single-instance) report pages enable it; the analytics
   *  page mounts this card twice and would fight over the same params. */
  deepLink?: boolean;
  /** Миниатюры над полосками. В недельном отчёте выключены: фото — только в Статистике (Алексей 28.10 #3065). */
  thumbs?: boolean;
}) {
  const { t } = useT();
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const [openTable, setOpenTable] = useState<TableStripTable | null>(null);
  // Preset zoom for the fullscreen view: click a specific interval on a
  // week strip → open zoomed to THAT day's working window.
  const [openZoom, setOpenZoom] = useState<[number, number] | null>(null);
  const [origAspect, setOrigAspect] = useState<boolean>(() => {
    try { return localStorage.getItem(ORIG_ASPECT_KEY) === '1'; } catch { return false; }
  });
  const toggleOrigAspect = () => setOrigAspect((v) => {
    try { localStorage.setItem(ORIG_ASPECT_KEY, v ? '0' : '1'); } catch { /* приватное окно */ }
    return !v;
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [linkRestored, setLinkRestored] = useState(false);
  // Remember the incoming deep-link params until we've consumed them. Captured
  // during RENDER (every render until restored, not just at mount) so it's
  // robust to the card mounting a tick after the URL has settled — the strips
  // data loads async and the report page normalises date/zone around the same
  // time, so a mount-only snapshot of window.location could miss them.
  const pendingLink = useRef<{ table: string; fromTs: string | null; toTs: string | null } | null>(null);
  if (deepLink && !linkRestored) {
    const tp = searchParams.get('table');
    if (tp) {
      pendingLink.current = {
        table: tp,
        fromTs: searchParams.get('from_ts'),
        toTs: searchParams.get('to_ts'),
      };
    }
  }

  const { data, error, isLoading, mutate } = useSWR(
    zoneId ? ['analytics:tables-strips', zoneId, from, to] : null,
    () => getTablesStrips(zoneId, from, to).then((r) => r.data),
    { keepPreviousData: false, revalidateOnFocus: false },
  );

  const tables = useMemo(() => data?.tables ?? [], [data]);

  // Restore the shared incident (table + fullscreen zoom window) from the URL
  // once the tables have loaded — a shareable "incident link". Runs once.
  useEffect(() => {
    if (!deepLink || linkRestored) return;
    if (!data || !data.tables || !data.tables.length) return; // wait for ids
    // Publish readiness with openTable/openZoom in the same render so URL sync
    // cannot clear the incoming link while restoration is still pending.
    setLinkRestored(true);
    const link = pendingLink.current;
    if (!link) return;
    const tbl = tables.find((x) => String(x.id) === link.table);
    if (!tbl) return;
    const f = link.fromTs ? new Date(link.fromTs).getTime() : NaN;
    const tt = link.toTs ? new Date(link.toTs).getTime() : NaN;
    setOpenZoom(Number.isFinite(f) && Number.isFinite(tt) && tt > f ? [f, tt] : null);
    setOpenTable(tbl);
  }, [deepLink, data, tables, linkRestored]);

  // Refresh the open table when its history changes.
  useEffect(() => {
    setOpenTable((cur) => (cur ? tables.find((x) => x.id === cur.id) ?? cur : cur));
  }, [tables]);

  // «Назад» в браузере закрывает модалку, а не уводит со страницы (Алексей 28.10 #3136/#3137):
  // открытие стола — новая запись истории (push), зум внутри — replace; ушёл `table` из URL
  // (кнопка «назад») — закрываем.
  const urlTable = searchParams.get('table');
  useEffect(() => {
    if (!deepLink || !linkRestored) return;
    if (!urlTable && openTable) {
      setOpenTable(null);
      setOpenZoom(null);
      setHover(null);
    }
  }, [urlTable, linkRestored]);

  // Mirror the open table + zoom window back into the URL. IDEMPOTENT: writes only when the URL
  // actually differs, so the restore frame (URL already matches) never triggers a write
  // and there's no way to clobber the params we just restored from.
  useEffect(() => {
    if (!deepLink || !linkRestored) return;
    const wantTable = openTable ? String(openTable.id) : null;
    const wantFrom = openTable && openZoom ? new Date(openZoom[0]).toISOString() : null;
    const wantTo = openTable && openZoom ? new Date(openZoom[1]).toISOString() : null;
    if (
      searchParams.get('table') === wantTable &&
      searchParams.get('from_ts') === wantFrom &&
      searchParams.get('to_ts') === wantTo
    ) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (wantTable) next.set('table', wantTable);
        else next.delete('table');
        if (wantFrom) next.set('from_ts', wantFrom);
        else next.delete('from_ts');
        if (wantTo) next.set('to_ts', wantTo);
        else next.delete('to_ts');
        return next;
      },
      { replace: !(wantTable && !searchParams.get('table')) },
    );
  }, [deepLink, openTable, openZoom, linkRestored]);

  const domain = useMemo<[number, number] | null>(() => {
    if (!data) return null;
    const wh = data.working_hours;
    const dom = wh ? whDomain(wh) : null; // padded VISUAL domain (888)
    const s = new Date(dom?.start ?? data.from).getTime();
    const e = new Date(dom?.end ?? data.to).getTime();
    return Number.isFinite(s) && Number.isFinite(e) && e > s ? [s, e] : null;
  }, [data]);

  // Per-day working windows (ms) — off-hours shading/tooltip + click→day.
  const workWindows = useMemo<Array<[number, number]>>(() => {
    return (data?.working_hours_by_day ?? [])
      .map((w): [number, number] => [new Date(w.start).getTime(), new Date(w.end).getTime()])
      .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e) && e > s);
  }, [data]);

  // Fullscreen photos: a per-window detail request — crops spread EVENLY over
  // the window (calm frames included, so zooming a green interval still shows
  // photos) + the first/last crop of every dirty stretch. Each dirty crop gets
  // its OWN orange underline (Alexey 2026-07-21) — the per-incident `group`
  // drew ONE continuous bar spanning several photos, which read as a single
  // strip; per-photo bars are clearer.
  const loadTableWindowPhotos = useCallback(
    async (table: TableStripTable, win: [number, number], count: number): Promise<DayStripPhoto[]> => {
      const r = await getTablesStrips(zoneId, from, to, table.id, {
        fromTs: new Date(win[0]).toISOString(),
        toTs: new Date(win[1]).toISOString(),
        photoCount: count,
      });
      const tbl = r.data.tables[0];
      // «В оригинальных пропорциях» — готовая картинка с сервера и в полосе, и по клику (#3082),
      // включая кропы, вырезанные на лету: формат один на весь график.
      const orig = origAspect && !!origGeomFromPolygon(table.polygon, { anyPolygon: true });
      return (tbl?.photos ?? []).map((ph) => ({
        image_url: orig ? ph.orig_image_url ?? ph.image_url : ph.image_url,
        frameUrl: ph.frame_image_url ?? null,
        bgFrameUrl: null,
        at: ph.at,
        // No text chip over the photo — the orange marker already says "не убран".
        // Кадр под статусом «нельзя определить» — чип с причиной (несколько столов /
        // перекрыт / засвет): серая полоса без фото и подписи ничего не объясняла (Дима 17.10).
        metric: ph.reason ? t(`tables.spoiledReason.${ph.reason}`) : null,
        dip: !!ph.dirty,
        durationS: ph.segment_s ?? null,
      }));
    },
    [zoneId, from, to, t, origAspect],
  );

  // Геометрия столов на кадре (любой полигон → описывающий прямоугольник), #2638.
  const geomById = useMemo(() => {
    const out = new Map<number, OrigGeom>();
    for (const tbl of data?.tables ?? []) {
      const g = origGeomFromPolygon(tbl.polygon, { anyPolygon: true });
      if (g) out.set(tbl.id, g);
    }
    return out;
  }, [data?.tables]);

  const origToggle = (
    <Tooltip content="Показывать фото в пропорциях стола на кадре камеры"><button
      type="button"
      aria-pressed={origAspect}
      onClick={toggleOrigAspect}
      className={cn(
        'shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        origAspect
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border bg-background text-muted-foreground hover:text-foreground',
      )}
    >
      Фото в оригинальных пропорциях
    </button></Tooltip>
  );
  // First load → the card renders with its real header right away (same style
  // as the other report cards) and only the strip area pulses as a skeleton.
  if (!data && isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className={CARD_TITLE}>{title}</CardTitle>
            {experimental && (
              <Badge variant="secondary" className="font-normal">
                {t('report.tables.newFeature')}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StateLegend />
            {origToggle}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-40 animate-pulse rounded-xl bg-muted" />
        </CardContent>
      </Card>
    );
  }
  if (error) return (
    <Card>
      <CardHeader><CardTitle className={CARD_TITLE}>{title}</CardTitle></CardHeader>
      <CardContent role="alert" className="flex items-center gap-3 text-sm">
        <span>{t('dashboard.apiMissing')}</span>
        <button type="button" className="text-primary underline" onClick={() => mutate()}>{t('common.retry')}</button>
      </CardContent>
    </Card>
  );
  if (!data || !data.configured) return null;
  if (!tables.length || !domain) return (
    <Card>
      <CardHeader><CardTitle className={CARD_TITLE}>{title}</CardTitle></CardHeader>
      <CardContent className="text-sm text-muted-foreground">{t('dashboard.status.noData')}</CardContent>
    </Card>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className={CARD_TITLE}>{title}</CardTitle>
          {experimental && (
            <Badge variant="secondary" className="font-normal">
              {t('report.tables.newFeature')}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <StateLegend />
          {origToggle}
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {tables.map((tbl) => {
          const openWholePeriod = () => {
            setOpenZoom(null);
            setOpenTable(tbl);
          };
          return (
            <div key={tbl.id}>
            {/* Маленькие фото над полоской — по времени съёмки (Алексей 25.10, #2345: «чтобы не просто график шёл»). */}
            {thumbs && (
            <div className="flex w-full items-end gap-2 px-1 pt-1.5">
              <span className="w-24 shrink-0 sm:w-32" />
              <TableThumbs zoneId={zoneId} from={from} to={to} table={tbl} domain={domain}
                origGeom={origAspect ? (geomById.get(tbl.id) ?? null) : null} />
              <span className="w-[18px] shrink-0" />
            </div>
            )}
            <div
              className="group flex w-full items-center gap-2 rounded-md px-1 py-0.5 transition-colors hover:bg-muted/50"
            >
              {/* Table name → the WHOLE period fullscreen. */}
              <Tooltip content={tbl.spoiled_reason
                    ? `${t('report.tables.stateUnknown')}: ${t(`tables.spoiledReason.${tbl.spoiled_reason}`)}`
                    : undefined}><button
                type="button"
                onClick={openWholePeriod}
                className="w-24 shrink-0 truncate text-left text-xs text-foreground sm:w-32"
              >
                {/* «состояние нельзя определить» — серый маркер перед именем, причина в title */}
                {tbl.spoiled_reason ? <span className="mr-1 text-slate-400">◌</span> : null}
                {tbl.name}
              </button></Tooltip>
              <StripBand
                segments={tbl.segments}
                domain={domain}
                height={18}
                onHover={setHover}
                workWindows={workWindows}
                // Click ANY segment (any state) → fullscreen zoomed to THAT
                // segment ±10% of its span (Alexey 2026-07-21). A click on a
                // gap (no segment) falls back to the whole period.
                onPick={(ms) => {
                  const seg = tbl.segments.find((s) => {
                    const sMs = new Date(s.start).getTime();
                    const eMs = new Date(s.end).getTime();
                    return Number.isFinite(sMs) && Number.isFinite(eMs) && ms >= sMs && ms <= eMs;
                  });
                  if (seg && domain) {
                    const sMs = new Date(seg.start).getTime();
                    const eMs = new Date(seg.end).getTime();
                    const pad = Math.max((eMs - sMs) * 0.1, 60_000);
                    setOpenZoom([Math.max(domain[0], sMs - pad), Math.min(domain[1], eMs + pad)]);
                  } else {
                    setOpenZoom(null); // whole period
                  }
                  setOpenTable(tbl);
                }}
              />
              {/* Dedicated «весь период» button, to the RIGHT of the row. */}
              <button
                type="button"
                onClick={openWholePeriod}
                aria-label={t('report.day.expand')}
                className="shrink-0 rounded p-0.5 text-muted-foreground/40 transition-colors hover:text-primary group-hover:text-muted-foreground"
              >
                <Maximize2 className="size-3.5" />
              </button>
            </div>
            </div>
          );
        })}
        <div className="flex items-center gap-2 pt-0.5">
          <span className="w-24 shrink-0 sm:w-32" />
          <div className="w-full">
            <StripAxis domain={domain} />
          </div>
        </div>
      </CardContent>

      {/* One shared floating tooltip — faster than a portal per segment.
          Near the right screen edge it flips to the pointer's LEFT so it
          never adds horizontal scroll. */}
      {hover && (
        <div
          className="pointer-events-none fixed z-[70] rounded-lg border border-border bg-card px-2 py-1 text-xs text-foreground shadow-md"
          style={
            hover.x > window.innerWidth - 240
              ? { right: window.innerWidth - hover.x + 12, top: hover.y + 12 }
              : { left: hover.x + 12, top: hover.y + 12 }
          }
        >
          {hover.state === 'nodata' || hover.state === 'offhours' ? (
            <div className="font-medium">
              {t(hover.state === 'offhours' ? 'report.tables.stateOffHours' : 'report.tables.stateNoData')}
            </div>
          ) : (
            <>
              <div className="font-medium">{t(STATE_LABEL_KEY[hover.state])}</div>
              {hover.state === 'unknown' && hover.reason && (
                <div className="text-muted-foreground">{t(`tables.spoiledReason.${hover.reason}`)}</div>
              )}
              {hover.state !== 'unknown' && hover.sub && SUB_REASON_KEY[hover.sub] && (
                <div className="text-muted-foreground">{t(SUB_REASON_KEY[hover.sub])}</div>
              )}
              {hover.startMs != null && hover.endMs != null && (
                <div className="text-muted-foreground">
                  {fmtClock(hover.startMs)}–{fmtClock(hover.endMs)} · {fmtDuration(t, hover.endMs - hover.startMs)}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {openTable && (() => {
        // Лайтбокс стола (Алексей 26.10 #2638/#2636/#2637): в «оригинальных пропорциях» — тот же
        // прямоугольник кадра, что в полосе; удержание — полный кадр с контуром стола в обоих режимах.
        // Кадра нет — как раньше, сохранённый кроп.
        const geom = geomById.get(openTable.id) ?? null;
        const poly = openTable.polygon && openTable.polygon.length >= 3 ? openTable.polygon : null;
        const origOn = origAspect && !!geom;
        return (
        <DayChartLightbox
          title={`${title} · ${openTable.name}`}
          photos={[]}
          captionOf={(p) => {
            const at = new Date(p.at);
            const time = at.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
            // «не убран 5 мин» — сколько длился этот отрезок полоски (Алексей 28.10 #3132).
            const state = p.dip
              ? ` · ${t('report.tables.stateDirty')}${fmtDirtyFor(p.durationS)}`
              : p.metric
                ? ` · ${t('report.tables.stateUnknown')}: ${p.metric}`
                : '';
            return `${openTable.name}${state} · ${time}`;
          }}
          fullDomain={domain}
          initialZoom={openZoom}
          plotInset={{ left: 0, right: 0, bottom: 18 }}
          renderChart={({ domain: dom, zoomTo }) => (
            <div className="space-y-1 pb-0.5">
              {/* В полноэкранном тоже: подсказка по наведению и клик по участку — приблизить его
                  ±10 % длины (Алексей 28.10 #3076). */}
              <StripBand
                segments={openTable.segments}
                domain={dom}
                height={36}
                onHover={setHover}
                workWindows={workWindows}
                onPick={zoomTo ? (ms) => {
                  const seg = openTable.segments.find((s) => ms >= new Date(s.start).getTime() && ms <= new Date(s.end).getTime());
                  if (!seg) return;
                  const sMs = new Date(seg.start).getTime(); const eMs = new Date(seg.end).getTime();
                  // Тот же отступ, что у клика по полоске на карточке: одно окно — одни и те же фото (#3080).
                  const pad = Math.max((eMs - sMs) * 0.1, 60_000);
                  zoomTo([sMs - pad, eMs + pad]);
                } : undefined}
              />
              <StripAxis domain={dom} />
            </div>
          )}
          loadWindowPhotos={(dom, count) => loadTableWindowPhotos(openTable, dom, count)}
          stripLanes={2}
          // Keep openZoom tracking the live marquee window so the deep-link URL
          // reflects the CURRENT incident view, not just the one it opened at.
          onZoomChange={deepLink ? (z) => setOpenZoom(z) : undefined}
          aspectOverride={origOn && geom ? origBoxAspect(geom, FRAME_ASPECT) : null}
          // Картинки уже в нужной проекции (сервер) — полоса рисует их как есть.
          origGeom={null}
          contentKey={`${openTable.id}:${origOn ? 'orig' : 'crop'}`}
          lightboxFrameOf={(p) => {
            if (!poly || !p.frameUrl) return null;
            const b = geom?.bbox;
            return {
              frameSrc: p.frameUrl,
              polygons: [poly],
              crop: origOn && b
                ? { frameSrc: p.frameUrl, polygons: [[[b.x0, b.y0], [b.x1, b.y0], [b.x1, b.y1], [b.x0, b.y1]]] }
                : null,
            };
          }}
          lightboxPrimary="crop"
          holdHint="Удерживайте — полный кадр"
          onClose={() => {
            setOpenTable(null);
            setOpenZoom(null);
            setHover(null);
          }}
        />
        );
      })()}
    </Card>
  );
}
